import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Popconfirm, Divider, Spin, Tabs, Button, message } from 'antd';
import OrganisationMainSection from '../../../../components/OrganisationMainSection/OrganisationMainSection';
import Modal from '../../../../components/Modal/Modal';
import InfoBox from '../../../../components/InfoBox/InfoBox';
import ModuleEmptyState from '../../../../components/ModuleEmptyState';
import Table from '../../../../components/Table/Table';
import ProductEdit from './components/ProductEdit';
import { fetchProduct, deleteProduct, fetchStrategicCriticalAreaCategories } from './ProductAction';
import { orgIdSelector } from '../../OrganisationSelectors';
import {
  productsSelector,
  strategicCriticalAreaCategoriesSelector,
  loadingStrategicAreaCategoriesSelector,
  loadingStrategicAreasSelector
} from './ProductSelectors';
import explanations from './data/explanations.json';
import { MSG_ERROR } from '../../../../constants';
import { getApiErrorMessage } from '../../../../utils/httpError';
import './Product.scss';

const { TabPane } = Tabs;

function getCategoryGuidance(categoryName) {
  return (
    <>
      <strong>What to do:</strong> record <strong>{categoryName}</strong> entries using <strong>Add new</strong> or edit
      from the table below.
    </>
  );
}
const INFO_MSG = `This area of the site is designed to identify and record the key pieces of equipment, product lines, services and other strategically important components of the organisation. These are areas that with their loss it would significantly damage the organisation's brand or the ongoing viability of the organisation.`;

const TOOLTIP_DETAIL = <p>{INFO_MSG}</p>;

const GUIDANCE = (
  <>
    <strong>What to do:</strong> use each tab&apos;s category, then capture strategic areas with <strong>Add new</strong>{' '}
    or edit them from the table.
  </>
);

class Product extends Component {
  state = {
    activeCategoryKey: null,
    activeCategoryId: null
  };

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    const { orgId, fetchProduct, fetchStrategicCriticalAreaCategories } = this.props;
    const { activeCategoryKey } = this.state;
    fetchProduct(orgId);
    await fetchStrategicCriticalAreaCategories();
    const { strategicCriticalAreaCategories } = this.props;
    if (strategicCriticalAreaCategories && strategicCriticalAreaCategories.length > 0 && !activeCategoryKey) {
      const nextKey = strategicCriticalAreaCategories[0].name;
      const nextId = strategicCriticalAreaCategories[0].strategicCriticalAreaCategoryId;
      this.setState({ activeCategoryKey: nextKey, activeCategoryId: nextId });
    }
  };

  onChange = key => {
    const { strategicCriticalAreaCategories = [] } = this.props;
    const activeCategory = strategicCriticalAreaCategories.find(item => item.name === key);
    const activeCategoryId = activeCategory.strategicCriticalAreaCategoryId;
    this.setState({ activeCategoryKey: key, activeCategoryId });
  };

  onDelete = async product => {
    const { orgId, deleteProduct } = this.props;
    try {
      await deleteProduct(orgId, product.strategicCriticalAreaId);
      message.success('The strategic area has been successfully deleted');
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  render() {
    const { products, strategicCriticalAreaCategories = [], loading } = this.props;
    const { activeCategoryKey, activeCategoryId } = this.state;
    const columns = [
      { title: 'Name', dataIndex: 'name', key: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
      { title: 'Category', key: 'category', render: (text, record) => record.strategicCriticalAreaCategory.name },
      { title: 'Description', dataIndex: 'description', key: 'description' },
      {
        key: 'action',
        width: 115,
        render: (text, record) => (
          <span>
            <Modal title={activeCategoryKey} toggle={show => <Button type="link" icon="edit" onClick={show} />}>
              {hide => <ProductEdit productInfo={record} activeCategoryId={activeCategoryId} close={hide} />}
            </Modal>
            <Divider type="vertical" />
            <Popconfirm title="Sure to delete?" onConfirm={() => this.onDelete(record)}>
              <Button type="link" icon="delete" />
            </Popconfirm>
          </span>
        )
      }
    ];

    const toolbarActions =
      activeCategoryId != null ? (
        <Modal
          title={activeCategoryKey}
          toggle={show => (
            <Button
              type="primary"
              className="organisation-main-section__toolbar-primary"
              htmlType="button"
              onClick={show}
            >
              Add new
            </Button>
          )}
        >
          {hide => <ProductEdit activeCategoryId={activeCategoryId} close={hide} />}
        </Modal>
      ) : null;

    const addStrategicAreaModal = (category, button) => (
      <Modal title={category.name} toggle={show => button(show)}>
        {hide => (
          <ProductEdit
            activeCategoryId={category.strategicCriticalAreaCategoryId}
            close={hide}
          />
        )}
      </Modal>
    );

    return (
      <OrganisationMainSection
        title="Strategic Areas"
        tooltipContent={TOOLTIP_DETAIL}
        guidance={GUIDANCE}
        toolbarActions={toolbarActions}
        flowPrev="/organization/strategic-crisis-experts"
        flowNext="/organization/policy"
      >
        <Spin spinning={loading}>
          <Tabs className="tabs" onChange={this.onChange}>
            {strategicCriticalAreaCategories.map(item => (
              <TabPane tab={item.name} key={item.name}>
                {explanations[item.name] && (
                  <InfoBox
                    color="#F1C400"
                    className="product-tab-guidance"
                    guidance={getCategoryGuidance(item.name)}
                    message={explanations[item.name]}
                  />
                )}
                <Table
                  title="Strategic Areas"
                  columns={columns}
                  dataSource={products[item.name]}
                  rowKey="strategicCriticalAreaId"
                  searchFields={['name', 'category', 'description']}
                  moduleEmpty={
                    <ModuleEmptyState
                      moduleTitle="Strategic Areas"
                      embedded
                      headline={`No ${item.name.toLowerCase()} strategic areas yet`}
                      description="Add the key services, equipment, product lines or other strategically important areas that this organisation depends on."
                      iconType="environment"
                    >
                      {addStrategicAreaModal(item, show => (
                        <Button
                          type="primary"
                          className="module-empty-state__btn-primary"
                          htmlType="button"
                          onClick={show}
                        >
                          Add First Strategic Area
                        </Button>
                      ))}
                    </ModuleEmptyState>
                  }
                />
              </TabPane>
            ))}
          </Tabs>
        </Spin>
      </OrganisationMainSection>
    );
  }
}

const mapStateToProps = state => ({
  orgId: orgIdSelector(state),
  products: productsSelector(state),
  strategicCriticalAreaCategories: strategicCriticalAreaCategoriesSelector(state),
  loading: loadingStrategicAreaCategoriesSelector(state) || loadingStrategicAreasSelector(state)
});

const mapDispatchToProps = {
  fetchProduct,
  deleteProduct,
  fetchStrategicCriticalAreaCategories
};

export default connect(mapStateToProps, mapDispatchToProps)(Product);
